/**
 * Will-call pickup rules — CLIENT mirror of
 * netlify/functions/_lib/will-call.ts (which is the source of truth;
 * submit-order re-validates server-side). Keep the two in sync.
 *
 * Rules: pickup window 9:30 AM – 3:00 PM Mon–Fri; no same-day will-call
 * after 12:00 PM warehouse time (America/Los_Angeles).
 */

export const WILL_CALL_WINDOW = '9:30 AM – 3:00 PM, Monday–Friday';
export const WILL_CALL_CUTOFF_HOUR = 12;

const TZ = 'America/Los_Angeles';

export function warehouseNow(now: Date = new Date()): { date: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const hour = Number(get('hour')) % 24;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, hour };
}

export function isWeekday(dateStr: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return false;
  const dow = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
  return dow >= 1 && dow <= 5;
}

function addDays(dateStr: string, days: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)!;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  return d.toISOString().slice(0, 10);
}

/** The earliest pickup date available right now (skips weekends + the
 * same-day noon cutoff). Use as the date input's `min`. */
export function earliestWillCallDate(now: Date = new Date()): string {
  const { date, hour } = warehouseNow(now);
  let candidate = date;
  if (hour >= WILL_CALL_CUTOFF_HOUR) candidate = addDays(candidate, 1);
  while (!isWeekday(candidate)) candidate = addDays(candidate, 1);
  return candidate;
}

export function validateWillCallDate(
  dateStr: string | undefined | null,
  now: Date = new Date(),
): { ok: true } | { ok: false; error: string } {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return { ok: false, error: 'Pick a pickup date.' };
  }
  if (!isWeekday(dateStr)) {
    return { ok: false, error: `Will-call is open Monday–Friday only (${WILL_CALL_WINDOW}).` };
  }
  const { date: today, hour } = warehouseNow(now);
  if (dateStr < today) {
    return { ok: false, error: 'That date has passed — pick a future weekday.' };
  }
  if (dateStr === today && hour >= WILL_CALL_CUTOFF_HOUR) {
    return {
      ok: false,
      error: 'Same-day will-call closes at 12:00 PM — pick the next business day.',
    };
  }
  return { ok: true };
}

/** "Fri, Aug 14" for a plain calendar date (UTC-anchored per the 1.21 rule). */
export function fmtPickupDate(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return dateStr;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(
    'en-US',
    { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' },
  );
}
